import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { resolveMediaUrl } from "@/lib/media";
import { toast } from "sonner";

const StoreContext = createContext(null);
const CART_KEY = "store_cart";
const WISH_KEY = "store_wishlist";

function readLS(key, fallback) {
  try {
    return JSON.parse(localStorage.getItem(key)) || fallback;
  } catch {
    return fallback;
  }
}

export function StoreProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState(null);
  const [cart, setCart] = useState(() => readLS(CART_KEY, []));
  const [wishlist, setWishlist] = useState(() => readLS(WISH_KEY, []));
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  // Store identity/contact details come from the backend only. A failure is
  // recorded here (and logged centrally in lib/api.js) instead of being swallowed,
  // so the UI can show a neutral "unavailable" state rather than fake placeholders.
  useEffect(() => {
    let cancelled = false;
    api.get("/settings")
      .then(({ data }) => { if (!cancelled) { setSettings(data); setSettingsError(null); } })
      .catch((err) => { if (!cancelled) setSettingsError(apiError(err)); })
      .finally(() => { if (!cancelled) setSettingsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => localStorage.setItem(CART_KEY, JSON.stringify(cart)), [cart]);
  useEffect(() => localStorage.setItem(WISH_KEY, JSON.stringify(wishlist)), [wishlist]);

  // The favicon is configurable in Admin → Settings. Once the store settings
  // arrive it is applied to the document (best effort: a missing/broken icon is
  // harmless and is served through the same media resolver as every other image).
  useEffect(() => {
    const favicon = settings?.business?.favicon;
    if (!favicon) return;
    let link = document.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = resolveMediaUrl(favicon);
  }, [settings]);

  // Authoritative cart pricing (product subtotal + the single automatic
  // discount) is recalculated by the backend every time the cart changes, so the
  // cart/checkout breakdown always matches what checkout will charge. Only
  // productId/variantId/quantity are sent — prices and discounts are computed
  // server-side and are never trusted from the browser.
  useEffect(() => {
    if (cart.length === 0) { setQuote(null); return; }
    let active = true;
    setQuoteLoading(true);
    api.post("/cart/quote", {
      items: cart.map((i) => ({ productId: i.productId, variantId: i.variantId, quantity: i.quantity })),
    }).then(({ data }) => { if (active) setQuote(data); })
      .catch((err) => { if (active) setQuote(null); console.error("Cart pricing unavailable:", err?.message); })
      .finally(() => { if (active) setQuoteLoading(false); });
    return () => { active = false; };
  }, [cart]);

  const addToCart = useCallback((item) => {
    setCart((prev) => {
      const idx = prev.findIndex((i) => i.productId === item.productId && i.variantId === item.variantId);
      let next;
      if (idx >= 0) {
        next = [...prev];
        const qty = Math.min(next[idx].quantity + item.quantity, item.stock);
        next[idx] = { ...next[idx], quantity: qty };
      } else {
        next = [...prev, { ...item, quantity: Math.min(item.quantity, item.stock) }];
      }
      return next;
    });
    toast.success("Added to cart", { description: item.name });
  }, []);

  const updateQty = useCallback((productId, variantId, quantity) => {
    setCart((prev) => {
      if (quantity <= 0) return prev.filter((i) => !(i.productId === productId && i.variantId === variantId));
      return prev.map((i) =>
        i.productId === productId && i.variantId === variantId
          ? { ...i, quantity: Math.min(quantity, i.stock) }
          : i
      );
    });
  }, []);

  const removeFromCart = useCallback((productId, variantId) => {
    setCart((prev) => prev.filter((i) => !(i.productId === productId && i.variantId === variantId)));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const toggleWishlist = useCallback((productId) => {
    setWishlist((prev) => {
      const exists = prev.includes(productId);
      toast[exists ? "message" : "success"](exists ? "Removed from wishlist" : "Added to wishlist");
      return exists ? prev.filter((id) => id !== productId) : [...prev, productId];
    });
  }, []);

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const cartSubtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  // No invented fallback branding: when settings have not loaded (or failed) this
  // stays null and consumers render a neutral loading/unavailable state.
  const business = settings?.business || null;

  return (
    <StoreContext.Provider
      value={{
        settings, settingsLoading, settingsError, business, cart, cartCount, cartSubtotal,
        addToCart, updateQty, removeFromCart, clearCart,
        wishlist, toggleWishlist, isWishlisted: (id) => wishlist.includes(id),
        quote, quoteLoading,
        autoDiscount: quote?.autoDiscount || null,
        quoteDiscount: quote?.discount || 0,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
}

export const useStore = () => useContext(StoreContext);
